import { FEATURES, type Feature, REVIEW_SOURCES } from './content';
import { LandingButton } from './LandingButton';
import { container, sectionHeading, sectionShell } from './landing.css';
import * as styles from './ProductCards.css';
import { START_FOR_FREE_LABEL } from './referral-copy';

interface ProductCardsProps {
  claimHref: string;
  onClaimClick?: () => void;
}

/**
 * The homepage's product tabs: one pill per product, fanned out behind the
 * card's own pill so the stack reads as "you are here" in the sequence.
 */
const TagStack = ({ index }: { index: number }) => (
  <div className={styles.tagStack}>
    {FEATURES.map((feature, position) => {
      const distance = Math.abs(position - index);
      return position === index ? (
        <span key={feature.key} className={styles.tag} style={{ zIndex: FEATURES.length }}>
          {feature.tag}
        </span>
      ) : (
        <span
          key={feature.key}
          className={styles.tagGhost}
          style={{ zIndex: FEATURES.length - distance }}
          aria-hidden
        />
      );
    })}
  </div>
);

const FeatureVideo = ({ feature }: { feature: Feature }) => (
  <div className={`${styles.media} ${styles.mediaTone[feature.mediaTone]}`}>
    <video
      className={styles.video}
      autoPlay
      loop
      muted
      playsInline
      preload="metadata"
      aria-hidden
      ref={(element) => {
        if (element) {
          element.muted = true;
        }
      }}
    >
      <source src={feature.video} type="video/mp4" />
    </video>
  </div>
);

const FeatureCard = ({
  feature,
  index,
  claimHref,
  onClaimClick,
}: ProductCardsProps & { feature: Feature; index: number }) => {
  const source = REVIEW_SOURCES[feature.quote.source];

  return (
    <li className={styles.card}>
      <div className={styles.text}>
        <div className={styles.heading}>
          <TagStack index={index} />
          <h3 className={styles.title}>{feature.title}</h3>
          <p className={styles.body}>{feature.body}</p>
        </div>
        <div className={styles.footer}>
          <figure className={styles.quote}>
            <img className={styles.quoteIcon} src={source.icon} alt="" />
            <figcaption className={styles.quoteText}>
              {feature.quote.text}{' '}
              <span className={styles.quoteAttribution}>{feature.quote.attribution}</span>
            </figcaption>
          </figure>
          <div className={styles.actions}>
            <LandingButton href={claimHref} size="compact" onClick={onClaimClick}>
              {START_FOR_FREE_LABEL}
            </LandingButton>
            {feature.explore ? (
              <LandingButton
                href={feature.explore.href}
                size="compact"
                tone="outline"
                target="_blank"
              >
                {feature.explore.label}
              </LandingButton>
            ) : null}
          </div>
        </div>
      </div>
      <FeatureVideo feature={feature} />
    </li>
  );
};

export const ProductCards = ({ claimHref, onClaimClick }: ProductCardsProps) => (
  <section className={`${sectionShell} ${styles.section}`}>
    <div className={container}>
      <h2 className={sectionHeading}>
        Convert first impressions
        <br />
        into connections that count
      </h2>
      <ol className={styles.cards}>
        {FEATURES.map((feature, index) => (
          <FeatureCard
            key={feature.key}
            feature={feature}
            index={index}
            claimHref={claimHref}
            onClaimClick={onClaimClick}
          />
        ))}
      </ol>
    </div>
  </section>
);
